import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, forkJoin, Observable, of, from, EMPTY } from 'rxjs';
import { flatMap, catchError, finalize, map } from 'rxjs/operators';

import { JwtHelperService } from '@auth0/angular-jwt';
import { FacebookLoginProvider, GoogleLoginProvider, SocialAuthService, SocialUser } from 'angularx-social-login';

import { AuthProxyService } from './auth-proxy.service';
import { StorageService } from '../../storage/storage.service';

import { StorageKey } from '../../storage/storage-keys';
import { UserCredentials, TokenRequestDto, TokenSocialRequestDto, ExternalLoginProviderType, UserRole } from '../models/auth-models';
import { MessagingService } from '../../notifications/messaging.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private DEFAULT_PROFILE_AVATAR = 'assets/images/global/default-avatar.svg';

	private userLogged = new BehaviorSubject<boolean>(false);

	public smAvatar = new BehaviorSubject<boolean>(false);

	private profileAvatar = new BehaviorSubject<SafeResourceUrl>(this.DEFAULT_PROFILE_AVATAR);

	private profileName = new BehaviorSubject<string>('anonymous');

	private userId = new BehaviorSubject<string>('');

	private externalUserId = new BehaviorSubject<string>('');

	private helper = new JwtHelperService();

	private userCounty = new BehaviorSubject<string>('');

	private roleParameterName = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

	get userLogged$(): Observable<boolean> {
		return this.userLogged.asObservable();
	}

	get userId$(): Observable<string> {
		return this.userId.asObservable();
	}

	get getProfileAvatar(): SafeResourceUrl {
		return this.profileAvatar.value;
	}

	setUserId(name: string) {
		return this.userId.next(name);
	}

	setExternalUserId(name: string) {
		return this.externalUserId.next(name);
	}

	get getExternalUserId(): string {
		return this.externalUserId.value;
	}

	get getUserId(): string {
		return this.userId.value;
	}

	get getProfileName(): string {
		return this.profileName.value;
	}

	setProfileName(name: string) {
		this.storageService.setItem(StorageKey.USERNAME, name);
		return this.profileName.next(name);
	}

	setDefaultProfileAvatar() {
		return this.profileAvatar.next(this.DEFAULT_PROFILE_AVATAR);
	}

	setProfileAvatar(base64: string) {
		return this.profileAvatar.next(this._sanitizer.bypassSecurityTrustResourceUrl(base64));
	}

	get userCountyFromStorage(): string {
		return this.userCounty.value;
	}

	setUserCounty(value: string) {
		this.storageService.setItem(StorageKey.USER_COUNTY, value);
		this.userCounty.next(value);
	}

	get isAuthenticated(): boolean {
		return this.userLogged.value;
	}

	get isSocialMediaAvatar(): boolean {
		return this.smAvatar.value;
	}

	get isExternalLoginProvider(): boolean {
		return this.storageService.getItemSync(StorageKey.LOGIN_TYPE) !== '';
	}

	get getSocialMediaProviderType(): string {
		return this.storageService.getItemSync(StorageKey.LOGIN_TYPE);
	}

	get getLocalAvatar() {
		return this._sanitizer.bypassSecurityTrustResourceUrl(this.storageService.getItemSync(StorageKey.AVATAR_URL));
	}

	get getLocalUserName(): string {
		return this.storageService.getItemSync(StorageKey.USERNAME);
	}

	get getLocalUserId(): string {
		return this.storageService.getItemSync(StorageKey.USERID);
	}

	get getLocalExternalUserId(): string {
		return this.storageService.getItemSync(StorageKey.EXTERNAL_USER_ID);
	}

	constructor(
		private router: Router,
		private storageService: StorageService,
		private authProxyService: AuthProxyService,
		private msgService: MessagingService,
		private _sanitizer: DomSanitizer,
		private socAuthService: SocialAuthService
	) {
		console.log('Auth Service Instance Created');
		this.initUserState();
	}

	public getAccessToken() {
		return this.storageService.getItemSync(StorageKey.ACCESS_TOKEN_KEY);
	}

	public getUserRole() {
		return this.storageService.getItemSync(StorageKey.ROLE_TYPE);
	}

	public hasRole(role: UserRole): boolean {
		const userRoles = this.getUserRole();

		return userRoles && userRoles.includes(role);
	}

	public initUserState() {
		this.storageService
			.getItem(StorageKey.ACCESS_TOKEN_KEY)
			.subscribe(
				res => {
					this.userLogged.next(res !== null);
				},
				() => this.userLogged.next(false)
			);
		this.profileAvatar.next(this.getLocalAvatar);
		this.profileName.next(this.getLocalUserName);
		this.setUserId(this.getLocalUserId);
		this.setExternalUserId(this.getLocalExternalUserId);
	}

	public setStorageItems(res) {
		this.storageService.setItem(StorageKey.ACCESS_TOKEN_KEY, res.result.accessToken);
		this.storageService.setItem(StorageKey.REFRESH_TOKEN_KEY, res.result.refreshToken);
		if (res.result.avatar === '') {
			this.storageService.setItem(StorageKey.AVATAR_URL, this.DEFAULT_PROFILE_AVATAR);
		} else if (res.result.avatarUrl != null) {
			this.storageService.setItem(StorageKey.AVATAR_URL, res.result.avatarUrl);
		} else if (res.result.avatar != null) {
			this.storageService.setItem(StorageKey.AVATAR_URL, res.result.avatar);
		}
		this.storageService.setItem(StorageKey.LOGIN_TYPE, ExternalLoginProviderType.FACEBOOK);
		const decodedToken = this.helper.decodeToken(res.result.accessToken);
		this.storageService.setItem(StorageKey.ROLE_TYPE, decodedToken[this.roleParameterName]);
		this.storageService.setItem(StorageKey.USERNAME, res.result.userName);
		this.setProfileAvatar(this.storageService.getItemSync(StorageKey.AVATAR_URL));
		this.setProfileName(res.result.userName);
		this.storageService.setItem(StorageKey.USERID, res.result.userId);
		this.setUserId(res.result.userId);
		this.storageService.setItem(StorageKey.EXTERNAL_USER_ID, res.result.externalId);
		this.setExternalUserId(res.result.externalId);
	}

	public loginSocial(res: SocialUser, provider: ExternalLoginProviderType): Observable<TokenSocialRequestDto> {
		const tokenPayload = {
			'authProvider': provider,
			'providerKey': res.id,
			'providerAccessCode': res.authToken,
			'returnUrl': null,
			'singleSignIn': false,
			'avatarUrl': res.photoUrl,
		} as TokenSocialRequestDto;

		return this.authProxyService
			.tokenSocial(tokenPayload)
			.pipe(
				flatMap((tokenRes: any) => {
					this.setStorageItems(tokenRes);
					this.userLogged.next(true);
					this.useSocialMediaAvatar();
					return of(tokenRes);
				})
			);
	}


	public login(userCredentials: UserCredentials): Observable<TokenRequestDto> {
		const tokenPayload = {
			userNameOrEmailAddress: userCredentials.email,
			password: userCredentials.password,
			twoFactorVerificationCode: null,
			rememberClient: true,
			twoFactorRememberClientToken: null,
			singleSignIn: false,
			returnUrl: null
		} as TokenRequestDto;

		return this.authProxyService
			.token(tokenPayload)
			.pipe(
				flatMap(res => {
					this.setStorageItems(res);
					let avatar = this.DEFAULT_PROFILE_AVATAR;
					if (res.result.avatar !== '') {
						avatar = res.result.avatar;
					}
					this.setProfileAvatar(avatar);
					this.userLogged.next(true);
					return of(res.result);
				})
			);
	}

	public renewToken(): Observable<TokenRequestDto> | Observable<{}> {
		const getAccessToken = this.storageService.getItem(StorageKey.ACCESS_TOKEN_KEY);
		const getRefreshToken = this.storageService.getItem(StorageKey.REFRESH_TOKEN_KEY);

		return forkJoin(getAccessToken, getRefreshToken)
			.pipe(
				flatMap(([accessToken, refreshToken]) => {
					const tokenPayload = {
						refreshToken: refreshToken
					} as TokenRequestDto;
					return this.authProxyService.refreshToken(tokenPayload);
				}
				),
				flatMap(res => {
					this.storageService.setItemSync(StorageKey.ACCESS_TOKEN_KEY, res.result.accessToken);
					this.storageService.setItemSync(StorageKey.REFRESH_TOKEN_KEY, res.result.refreshToken);
					const decodedToken = this.helper.decodeToken(res.result.accessToken);
					this.storageService.setItemSync(StorageKey.ROLE_TYPE, decodedToken[this.roleParameterName]);
					return of(res);
				}),
				catchError(async (err) => await this.logout()),
			);
	}

	public async logout() {
		this.msgService.deleteToken();
		this.storageService.clearAll();
		this.setProfileAvatar(this.DEFAULT_PROFILE_AVATAR);
		this.userLogged.next(false);
		this.smAvatar.next(false);
		this.router.navigate(['account/login']);
		return EMPTY;
	}

	doSocialLogin(loginType: ExternalLoginProviderType) {
		switch (loginType) {
			case ExternalLoginProviderType.GOOGLE: {
				return this.socAuthService.signIn(GoogleLoginProvider.PROVIDER_ID).then((result) => {
					return this.loginSocial(result, ExternalLoginProviderType.GOOGLE).toPromise();
				});
			}
			case ExternalLoginProviderType.FACEBOOK: {
				return this.socAuthService.signIn(FacebookLoginProvider.PROVIDER_ID).then((result) => {
					return this.loginSocial(result, ExternalLoginProviderType.FACEBOOK).toPromise();
				});
			}
		}
	}

	public useSocialMediaAvatar() {
		const avatarUrl = this.storageService.getItemSync(StorageKey.AVATAR_URL);
		const avatarExist = !(avatarUrl === null || avatarUrl === undefined);
		this.smAvatar.next(avatarExist);
		if (this.getProfileAvatar === this.DEFAULT_PROFILE_AVATAR) {
			this.setProfileAvatar(avatarUrl);
		}
	}
}
